import { lzFIsObject } from '@lz/services/Type';
import { LzTOpenObject } from '@lz/typings';

export type LzTRightsTree = any;
export type LzTRights = '*' | '-' | LzTRightsTree | null;

class LzMRights {
  static TYPE_UNKNOWN = 0;
  static TYPE_ALLOW_ALL = 1;
  static TYPE_DENY_ALL = 2;
  static TYPE_CUSTOM = 3;

  static VALUE_ALLOW_ALL = '*';
  static VALUE_DENY_ALL = '-';

  /**
   * This is a constant that can be used to mark the super user only nodes.
   */
  static FOR_SUPER_USER_ONLY = '4suonly';

  static KEY_LABELS: LzTOpenObject = {
    // TODO: Add some mechanism to get this from actual project/app
    // NOTE: It works simply like this:
    //        - Add a key value pair like: emp_leaves_summ: 'Employee Leaves Summary',
    //        - Now the rights key 'emp_leaves_summ' will translate to label 'Employee Leaves Summary'
  };

  static parsedRights: LzTRights = null;

  static checkRight(rights: LzTRights, action: string): boolean {
    // If rights object not exists then deny
    if (!rights) {
      // console.log('NO RIGHTS!');
      return false;
    }

    // If allowed all by rights object
    if (LzMRights.VALUE_ALLOW_ALL === rights) {
      return true;
    }

    // If deny all by rights object
    if (LzMRights.VALUE_DENY_ALL === rights) {
      return false;
    }

    // Parse the rights if not already parsed
    LzMRights.reParseRights(rights);

    // Return the rights based on the parsing
    return LzMRights.parsedRights ? LzMRights.parsedRights[action] : false;
  }

  static reParseRights(rights: LzTRights, forceReParse?: boolean): void {
    // If rights not already parsed or we want to force parse them
    if (!LzMRights.parsedRights || forceReParse) {
      // Parse recursively
      LzMRights.parsedRights = LzMRights.parseRightsToArray('', rights);
      // console.log('RIGHTS PARSED', RightsModel.parsedRights, rights);
    }
  }

  static parseRightsToArray(parentKey: string, tree: LzTRightsTree): any {
    // console.log('RP', parentKey, tree);
    let out: any = {};
    if (!tree) {
      return out;
    }
    const keys = Object.keys(tree);

    for (const key of keys) {
      // console.log('RP', parentKey, key);
      if (typeof tree[key] === 'object') {
        const res = LzMRights.parseRightsToArray(
          parentKey + key + '.',
          tree[key]
        );
        // console.log('RES', Object.keys(res).length, res);
        if (Object.keys(res).length > 0) {
          // out[parentKey.substring(0, parentKey.length - 1)] = true;
          out[parentKey + key] = true;
          out = Object.assign({}, out, res);
          // console.log('OUT', out, res);
        }
      } else if (!!tree[key]) {
        // console.log('FOUND', parentKey + key, tree[key]);
        out[parentKey + key] = tree[key];
      }
    }

    return out;
  }

  static makeRightsBySchema(schema: any, value?: boolean): any {
    if (!schema || !schema.__) {
      return {};
    }

    const out: any = {};

    const keys = Object.keys(schema);
    for (const key of keys) {
      if (key !== '__') {
        out[key] = {};
        const keys2 = Object.keys(schema[key]);
        for (const key2 of keys2) {
          out[key][key2] = {};
          for (const a of schema[key][key2]) {
            out[key][key2][a] = !!value;
          }
        }
      }
    }

    return out;
  }

  static updateRightsBySchema(rights: any, schema: any): any {
    if (!lzFIsObject(rights)) {
      return LzMRights.makeRightsBySchema(schema);
    }

    if (!schema || !schema.__) {
      return {};
    }

    const out = rights;

    const keys = Object.keys(schema);
    for (const key of keys) {
      if (key !== '__') {
        out[key] = out[key] ? out[key] : {};
        const keys2 = Object.keys(schema[key]);
        for (const key2 of keys2) {
          out[key][key2] = out[key][key2] ? out[key][key2] : {};
          for (const a of schema[key][key2]) {
            out[key][key2][a] = !!out[key][key2][a];
          }
        }
      }
    }

    return out;
  }

  static getType(rights: any): number {
    if (!rights) {
      return LzMRights.TYPE_UNKNOWN;
    }

    if (LzMRights.VALUE_ALLOW_ALL === rights) {
      return LzMRights.TYPE_ALLOW_ALL;
    }

    if (LzMRights.VALUE_DENY_ALL === rights) {
      return LzMRights.TYPE_DENY_ALL;
    }

    return LzMRights.TYPE_CUSTOM;
  }

  static setByType(type: number, schema: any): string {
    if (LzMRights.TYPE_ALLOW_ALL === type) {
      return LzMRights.VALUE_ALLOW_ALL;
    }

    if (LzMRights.TYPE_DENY_ALL === type) {
      return LzMRights.VALUE_DENY_ALL;
    }

    return LzMRights.makeRightsBySchema(schema);
  }

  static getSchemaActions(schema: any): any {
    return schema.__;
  }

  static getSchemaGroups(schema: any): any {
    return Object.keys(schema)
      .filter((key) => key !== '__')
      .map((key) => key.replace(/_/gi, ' '));
  }

  static getSchemaGroupItemsAsKeys(schema: any, group: any): any {
    return Object.keys(schema[group]);
  }

  static getLabelFromKey(key: string) {
    return LzMRights.KEY_LABELS[key] || key.replace(/_/gi, ' ');
  }
}

export default LzMRights;
